
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Contact() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleBookClass = () => {
    // Trigger Readdy Agent widget for booking
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    } else {
      // Fallback to contact page
      navigate('/contact');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3hpqe15ls7rqlnfi93g', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: t('contact.form.email'),
      value: 'info@mathlish.aarivian.com',
      link: 'mailto:info@mathlish.aarivian.com'
    },
    {
      icon: 'ri-phone-line',
      title: t('contact.form.phone'),
      value: '+380 XX XXX XXXX',
      link: 'tel:+380XXXXXXXXX'
    },
    {
      icon: 'ri-map-pin-line',
      title: t('footer.location'),
      value: t('footer.location'),
      link: null
    },
    {
      icon: 'ri-time-line',
      title: t('contact.workingHours'),
      value: t('contact.schedule'),
      link: null
    }
  ];

  const faqs = [
    {
      question: t('contact.faq.booking.question'),
      answer: t('contact.faq.booking.answer')
    },
    {
      question: t('contact.faq.schedule.question'),
      answer: t('contact.faq.schedule.answer')
    },
    {
      question: t('contact.faq.technical.question'),
      answer: t('contact.faq.technical.answer')
    },
    {
      question: t('contact.faq.materials.question'),
      answer: t('contact.faq.materials.answer')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Communication Theme */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image - Communication & Connection Theme */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20modern%20communication%20scene%20with%20diverse%20team%20members%20collaborating%20in%20bright%20office%20environment%2C%20people%20connecting%20through%20technology%20and%20conversation%2C%20warm%20welcoming%20atmosphere%20with%20natural%20lighting%2C%20modern%20workspace%20with%20emerald%20green%20and%20teal%20color%20accents%2C%20teamwork%20and%20connection%20concept%20with%20smiling%20professionals%20engaged%20in%20meaningful%20discussion%2C%20bright%20contemporary%20office%20setting%20with%20large%20windows%20and%20natural%20light%20streaming%20in%2C%20collaborative%20workspace%20representing%20effective%20communication%20and%20partnership&width=1920&height=1080&seq=contact-hero-communication-v3&orientation=landscape"
            alt="Communication and teamwork"
            className="w-full h-full object-cover object-top"
          />
          {/* Overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/30 via-emerald-500/20 to-teal-600/30"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight drop-shadow-2xl">
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                {t('contact.title')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-12 font-medium leading-relaxed drop-shadow-xl max-w-4xl mx-auto">
              {t('contact.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button 
                size="lg" 
                className="px-12 py-6 text-xl font-bold border-2 border-white/30 rounded-xl"
                onClick={handleBookClass}
              >
                <i className="ri-calendar-check-line mr-3 text-xl"></i>
                {t('cta.bookClass')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
              {t('contact.getInTouch')}
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 font-medium">
              {t('contact.chooseMethod')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-white rounded-3xl p-10 shadow-2xl card-hover text-center border-2 border-emerald-100 hover:border-emerald-200">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-8 mx-auto shadow-lg">
                  <i className={`${info.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {info.title}
                </h3>
                {info.link ? (
                  <a 
                    href={info.link}
                    className="text-gray-600 hover:text-emerald-600 transition-colors text-lg font-medium"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-gray-600 text-lg font-medium">{info.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-8">
                {t('contact.sendMessage')}
              </h2>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contact">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-lg font-semibold text-gray-700 mb-3">
                      {t('contact.form.name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg transition-all duration-300 ease-in-out"
                      placeholder={t('contact.form.namePlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-lg font-semibold text-gray-700 mb-3">
                      {t('contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg transition-all duration-300 ease-in-out"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="phone" className="block text-lg font-semibold text-gray-700 mb-3">
                      {t('contact.form.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg transition-all duration-300 ease-in-out"
                      placeholder="+380 XX XXX XXXX"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-lg font-semibold text-gray-700 mb-3">
                      {t('contact.form.subject')} *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg pr-8 transition-all duration-300 ease-in-out"
                    >
                      <option value="">{t('contact.form.selectSubject')}</option>
                      <option value="enrollment">{t('contact.subjects.enrollment')}</option>
                      <option value="pricing">{t('contact.subjects.pricing')}</option>
                      <option value="schedule">{t('contact.subjects.schedule')}</option>
                      <option value="technical">{t('contact.subjects.technical')}</option>
                      <option value="other">{t('contact.subjects.other')}</option>
                    </select>
                  </div>
                </div>
                
                <div className="mb-6">
                  <label htmlFor="message" className="block text-lg font-semibold text-gray-700 mb-3">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg resize-none transition-all duration-300 ease-in-out"
                    placeholder={t('contact.form.messagePlaceholder')}
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('contact.form.maxChars')} ({formData.message.length}/500)
                  </p>
                </div>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-6 bg-green-50 border-2 border-green-200 rounded-xl">
                    <p className="text-green-700 text-lg font-medium">
                      <i className="ri-check-circle-line mr-3 text-xl"></i>
                      {t('contact.form.successMessage')}
                    </p>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="mb-6 p-6 bg-red-50 border-2 border-red-200 rounded-xl">
                    <p className="text-red-700 text-lg font-medium">
                      <i className="ri-error-warning-line mr-3 text-xl"></i>
                      {t('contact.form.errorMessage')}
                    </p>
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full py-5 text-xl font-bold rounded-xl"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-3 text-xl"></i>
                      {t('contact.form.sending')}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-3 text-xl"></i>
                      {t('contact.form.send')}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* FAQ Section */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-8">
                {t('contact.faq.title')}
              </h2>
              
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-10 shadow-xl border-2 border-emerald-100">
                <div className="space-y-8">
                  {faqs.map((faq, index) => (
                    <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-emerald-100 hover:border-emerald-200 card-hover">
                      <h4 className="font-bold text-gray-900 mb-4 text-xl">
                        <i className="ri-question-line text-emerald-600 mr-3 text-xl"></i>
                        {faq.question}
                      </h4>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
